import React from 'react';
import { CaretDownOutlined, CaretRightFilled, RightSquareOutlined } from '@ant-design/icons';
import DestinationIcon from '@components/common/icons/destinationIcon';
import SourceIcon from '@components/common/icons/sourceIcon';
import { ParsedLog } from '@stores/auditLogs';
import { Flex } from '@ui-library/misc';
import Tooltip from '@ui-library/tooltip';
import ReactJson from 'react-json-view';
import styles from './auditLogsData.module.scss';
import Text from '@ui-library/table/components/text';
import Table from '@ui-library/table';
import { TableBody } from '@ui-library/typography';

interface IAuditLogDataProps {
  loading?: string;
  logs?: ParsedLog[];
  lessInfo?: boolean;
}

const AuditLogsData = (props: IAuditLogDataProps) => {
  const loading = props.loading ? (
    <TableBody className={styles.loading}>{props.loading}</TableBody>
  ) : null;

  const data = (props.logs || []).map((log) => ({ ...log, key: log.id }));

  const columns = [
    {
      title: 'USER',
      dataIndex: 'userName',
      key: 'userName',
      render: (userName: string, log: ParsedLog) => (
        <>
          <span className={`${styles.capitalize} text-ellipsis`}>
            <Tooltip title={log.userName || ''}>{userName}</Tooltip>
          </span>
          <TableBody className={styles.email}>{log.email}</TableBody>
        </>
      ),
    },
    {
      title: 'ACTION',
      dataIndex: 'action',
      key: 'action',
      render: (action: string) => <Text className={styles.capitalize} name={action} />,
    },
    ...(!props.lessInfo
      ? [
          {
            title: 'TARGET',
            dataIndex: 'defName',
            key: 'defName',
            render: (defName: string, log: ParsedLog) => (
              <div className={styles.target}>
                {defName &&
                  (log.targetType === 'destination' ? (
                    <DestinationIcon destination={`${defName}`} />
                  ) : (
                    <SourceIcon source={`${defName}`} />
                  ))}
                <Text className={styles.target_name} name={log.target} />
              </div>
            ),
          },
          {
            title: 'TYPE',
            dataIndex: 'targetType',
            key: 'targetType',
            render: (targetType: string) => (
              <Text className={styles.capitalize} name={targetType} />
            ),
          },
        ]
      : []),
    {
      title: 'WHEN',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: string) => <Text name={createdAt} />,
    },
  ];

  return (
    <>
      <Table
        locale={{ emptyText: props.loading }}
        dataSource={data}
        columns={columns}
        expandRowByClick
        expandable={{
          expandedRowRender: (log: ParsedLog) => (
            <div role="details" className={styles.row_details}>
              <div style={{ marginBottom: '1rem' }}>JSON:</div>
              <Flex spaceBetween>
                {log.payload.from ? (
                  <>
                    <ReactJson
                      src={log.payload.from}
                      enableClipboard={false}
                      displayDataTypes={false}
                      name={null}
                      style={{
                        border: '1px solid #DEDEDE',
                        borderRadius: '4px',
                        width: '45%',
                      }}
                    />
                    <RightSquareOutlined
                      role="update_arrow"
                      style={{
                        alignSelf: 'center',
                        fontSize: '32px',
                        color: '#DEDEDE',
                      }}
                    />
                  </>
                ) : null}
                <ReactJson
                  src={log.payload.to}
                  enableClipboard={false}
                  displayDataTypes={false}
                  name={null}
                  style={{
                    border: '1px solid #DEDEDE',
                    borderRadius: '4px',
                    width: log.payload.from ? '45%' : '100%',
                  }}
                />
              </Flex>
            </div>
          ),
          expandIcon: ({ expanded, onExpand, record }) =>
            expanded ? (
              <CaretDownOutlined onClick={(e) => onExpand(record, e)} />
            ) : (
              <CaretRightFilled onClick={(e) => onExpand(record, e)} />
            ),
        }}
        pagination={false}
      />
      {props.logs?.length && loading ? loading : null}
    </>
  );
};

export default AuditLogsData;
